import { trackEvent } from '../utils/usage.js';
import { createStore } from 'vuex';
import { waitingPatch } from './asyncPlay';
import { signInWithPopup, signOut, onAuthStateChanged } from 'firebase/auth';
import { settleSharedSignIn, markInteractiveSignIn, endSharedSignIn } from '@/utils/sharedSignIn';
import {
  ref as dbRef, push, get, set, update, remove, onValue, onDisconnect,
} from 'firebase/database';
import { createGameState } from '@/engine/state/createGameState';
import { TUTORIAL_SEED } from '../tutorial/spaceBaseSteps';
import { dispatch as engineDispatch, ACTION_TYPES } from '@/engine/actions/index';
import {
  playAITurn, resolveAllocationForAI, resolvePendingSpends, tryUseReadyAbilities, resolveBonusRollForAI,
  resolveNeighborChoiceForAI,
} from '@/engine/ai/heuristicBot';
import { saveLocalGame, loadLocalGame, clearLocalGame } from './localGameStorage';
import { db, auth, googleProvider } from '../firebase';
import { generateRoomCode, normalizeRoomCode } from '../utils/roomCodes';
import { outcomeOf, fingerprintOf, recordFinishedGame } from './userStats';
import {
  ensureSiteUserRecord, watchSiteUserStatus, watchAllSiteUsers, setSiteUserStatus,
  cacheApprovedSiteUser, readCachedSiteUser,
} from './siteAccess';
import { readLastSignInEmail, writeLastSignInEmail } from './lastSignIn';

// Matt: "as long as you're doing that [adding an AI opponent], it'll be
// also great to be able to set up remote games. You can look to how we
// did it for Thunderstone to see how to do it, a similar pattern." Mirrors
// the thunderstone repo's own store/index.js remote-game architecture
// almost verbatim, under this repo's own already-reserved RTDB namespace
// (spaceBaseGames/spaceBaseGameCodes/spaceBaseUserGames - see
// database.rules.json's own comment for why that prefix exists) rather
// than colliding with thunderstone's own games/gameCodes/userGames keys
// in the same shared Firebase project.
const REMOTE_GAMES_KEY = 'spaceBaseGames';
const REMOTE_GAME_CODES_KEY = 'spaceBaseGameCodes';
const REMOTE_USER_GAMES_KEY = 'spaceBaseUserGames';

const unsubscribers = {};
const detach = (key) => {
  if (unsubscribers[key]) {
    unsubscribers[key]();
    delete unsubscribers[key];
  }
};

const AI_STEP_PAUSE_MS = 500;
const AI_STEP_LIMIT = 200; // generous safety cap, not a real turn/move budget - see driveAITurns' own comment.

// Offline-approval-gate fallback (see siteAccess.js's own comment on
// cacheApprovedSiteUser/readCachedSiteUser). "A few seconds is plenty" -
// long enough that a normal, merely-a-bit-slow live Firebase round trip
// still wins and shows the real (possibly non-approved) status, short
// enough that someone genuinely offline isn't left staring at the spinner
// for anything like the timeout RTDB's own websocket connection attempt
// would otherwise take.
const SITE_USER_FALLBACK_TIMEOUT_MS = 4000;

// Matt: "figure out how we can add an AI player into this so I can play
// against the computer... I should be able to play against as many
// computers as I want to." One unit of AI progress per call - the first
// of these that actually changes something wins:
//   1. Any AI player's (active OR passive) pending claim/extra-buy - some
//      cards grant these to a passive player too (l2-kovalyonok's deployed
//      reward, a red-timed buyCardSectorRange ability - see
//      turnActions.js's own comments on claimCard/buyCard), and they're
//      resolvable independent of whose turn it nominally is. If a given
//      player's pending item can't actually be resolved right now (e.g.
//      too poor to afford anything, or the claimed level is sold out),
//      resolvePendingSpends is a no-op for them and this loop moves on to
//      the next AI rather than getting stuck retrying it forever - see the
//      loop's own `continue`.
//   2. A pending dice allocation for an AI player (active or passive).
//   3. A passive AI player's own ready (charged) ability - playAITurn only
//      ever drives the ACTIVE player's turn, so without this a passive AI's
//      red/green ability would never fire on its own; mirrors what a human
//      already does by switching to their own board's tab at any time (see
//      GameBoard.vue's onUseAbility).
//   4. The active AI player's own turn (roll through end turn, or as much
//      of it as isn't blocked waiting on another player - see
//      heuristicBot.js's playAITurn for why "one call" can still mean
//      "stopped early, needs calling again").
// Returns the same state reference when there's genuinely nothing left for
// any AI to do right now (a human's turn, or a human's allocation/pending
// item is still outstanding).
function driveAITurnsStep(state) {
  // Matt reported this directly: "The game still isn't waiting for my
  // chained arrow choices when they trigger on other players turns. It
  // asks me which section I want to trigger but the computer just keeps
  // going." applyHit runs for every player, active or passive, on every
  // roll (see settleArrowChain's own comment) - so an AI's dice roll can
  // reactively queue a both-direction-arrow choice owed to a HUMAN who
  // isn't even the active player. Nothing here ever checked for that
  // before continuing to drive the active AI's turn forward, so the game
  // clock kept ticking underneath an unanswered human prompt. Checked
  // first, before anything else below - nothing here should make more
  // progress while a human is genuinely owed an answer, regardless of
  // whose turn it nominally is.
  // Matt reported this directly: "go through and do a full audit... it
  // should be a blocking decision. The flow of the game, the AI, nothing
  // else should happen until the decision is made." pendingClaims/
  // pendingExtraBuys/pendingSetupPlacements were the one gap this audit
  // found: endTurn() already blocks ending the turn on ANY player's own
  // entry here (turnActions.js's own comment), but nothing stopped
  // playAITurn from still rolling and buying for the ACTIVE ai player
  // first, racing right past a PASSIVE human's own still-unresolved claim
  // in the meantime - only the final END_TURN attempt was silently a
  // no-op, not the whole turn. Same reasoning/shape as every other
  // reactive-owed guard below - none of these three carry a turnPhase
  // gate of their own, so a human can resolve theirs immediately
  // regardless of whose turn it nominally is, same "no deadlock risk"
  // logic endTurn's own gate already relies on.
  const humanOwesClaim = (state.meta.pendingClaims || [])
    .some((c) => !state.players.find((p) => p.id === c.playerId)?.isAI);
  const humanOwesExtraBuy = (state.meta.pendingExtraBuys || [])
    .some((eb) => !state.players.find((p) => p.id === eb.playerId)?.isAI);
  const humanOwesSetupPlacement = (state.meta.pendingSetupPlacements || [])
    .some((sp) => !state.players.find((p) => p.id === sp.playerId)?.isAI);
  // pendingSectorPicks (Shy Pluto's "gain rewards for any N sectors 1-6" -
  // turnActions.js's own resolveSectorPick) is a further per-player
  // choice that can be owed reactively the exact same way an arrow choice
  // can (no real card uses it yet, but its own bonusRoll/pickSectors
  // "gain rewards like the active player" plumbing already lets it fire
  // on someone else's roll) - same fix, same reasoning as the arrow-
  // choice one right below.
  const humanOwesArrowChoice = (state.meta.pendingArrowChoices || [])
    .some((c) => !state.players.find((p) => p.id === c.playerId)?.isAI);
  const humanOwesSectorPick = (state.meta.pendingSectorPicks || [])
    .some((p) => !state.players.find((pl) => pl.id === p.playerId)?.isAI);
  // pendingDeckPeeks (Genesis's Dalton - turnActions.js's own
  // resolveDeckPeek) is a green-timing ability, so a passive player can
  // activate their own copy mid-someone-else's-turn same as any other
  // green ability - same racing risk, same guard.
  const humanOwesDeckPeek = (state.meta.pendingDeckPeeks || [])
    .some((p) => !state.players.find((pl) => pl.id === p.playerId)?.isAI);
  // pendingChargeChoices (Genesis's Helios/Radiant/Watts - turnActions.js's
  // own resolveChargeChoice) - same reactive-on-another-player's-roll
  // path as pendingSectorPicks above, same guard.
  const humanOwesChargeChoice = (state.meta.pendingChargeChoices || [])
    .some((p) => !state.players.find((pl) => pl.id === p.playerId)?.isAI);
  // pendingMoveChoices (Genesis's Triumph/Ulric - turnActions.js's own
  // resolveMoveChoice) - only ever granted via a buy's own immediateReward,
  // which a passive player can trigger through their own extraBuy mid
  // someone else's turn, same reactive-owed risk as everything else here.
  const humanOwesMoveChoice = (state.meta.pendingMoveChoices || [])
    .some((p) => !state.players.find((pl) => pl.id === p.playerId)?.isAI);
  // pendingCardSwapChoices (Genesis's Maddock - turnActions.js's own
  // resolveCardSwapChoice) - same immediateReward-via-extraBuy reactive
  // shape as pendingMoveChoices above, same guard.
  const humanOwesCardSwapChoice = (state.meta.pendingCardSwapChoices || [])
    .some((p) => !state.players.find((pl) => pl.id === p.playerId)?.isAI);
  // pendingEmptySectorChoices (Genesis's Pharos - turnActions.js's own
  // resolveEmptySectorChoice) - reachable through a normal dice-hit/
  // arrow-chain reward (not just an immediateReward), so any of the
  // usual reactive-owed paths (a passive player's own deployed card
  // getting hit via someone else's arrow) can owe this too.
  const humanOwesEmptySectorChoice = (state.meta.pendingEmptySectorChoices || [])
    .some((p) => !state.players.find((pl) => pl.id === p.playerId)?.isAI);
  // pendingCardGiftChoices (Genesis's Fitzgerald - turnActions.js's own
  // resolveCardGiftTarget) - same buy-time-broadcast reactive shape as
  // pendingEmptySectorChoices/pendingChargeChoices above, same guard.
  const humanOwesCardGiftChoice = (state.meta.pendingCardGiftChoices || [])
    .some((p) => !state.players.find((pl) => pl.id === p.playerId)?.isAI);
  // pendingBonusRolls (Genesis/Shy Pluto's Bonus Roll - turnActions.js's
  // own enqueueBonusRoll/resolveBonusRollChoice) now needs a real
  // sum/separate choice for EVERY roll instead of auto-resolving (Matt's
  // own "every decision needs to be blocking" report) - only the FRONT
  // entry is ever "live", so this only ever checks that one; if it
  // belongs to a human, nothing here should race ahead of them, same
  // reasoning as every other reactive-owed guard above.
  const humanOwesBonusRoll = Boolean(state.meta.pendingBonusRolls?.[0])
    && !state.players.find((p) => p.id === state.meta.pendingBonusRolls[0].playerId)?.isAI;
  // pendingNeighborChoices (Genesis's Flying Dutchman - turnActions.js's
  // own resolveNeighborStationReward/resolveNeighborChoice) - reads (not
  // modifies) another player's board, so unlike most reactive choices
  // here it's owed to whoever ROLLED into their OWN Flying Dutchman, not
  // to whoever's board got read - same reactive-owed risk as everything
  // else above regardless.
  const humanOwesNeighborChoice = (state.meta.pendingNeighborChoices || [])
    .some((c) => !state.players.find((pl) => pl.id === c.playerId)?.isAI);
  // Biodome/Satkowiak's own three new queues (turnActions.js's
  // resolveShipDiscard / resolveDieRewardChoice / resolveCardTradeChoice)
  // - Frye-Kova and Satkowiak explicitly queue choices for PASSIVE
  // players, so these carry exactly the same reactive-owed risk as
  // everything above, same guard.
  const humanOwesShipDiscard = (state.meta.pendingShipDiscards || [])
    .some((c) => !state.players.find((pl) => pl.id === c.playerId)?.isAI);
  const humanOwesDieReward = (state.meta.pendingDieRewardChoices || [])
    .some((c) => !state.players.find((pl) => pl.id === c.playerId)?.isAI);
  const humanOwesCardTrade = (state.meta.pendingCardTradeChoices || [])
    .some((c) => !state.players.find((pl) => pl.id === c.playerId)?.isAI);
  if (humanOwesClaim || humanOwesExtraBuy || humanOwesSetupPlacement || humanOwesArrowChoice || humanOwesSectorPick || humanOwesDeckPeek || humanOwesChargeChoice || humanOwesMoveChoice || humanOwesCardSwapChoice || humanOwesEmptySectorChoice || humanOwesCardGiftChoice || humanOwesBonusRoll || humanOwesNeighborChoice || humanOwesShipDiscard || humanOwesDieReward || humanOwesCardTrade) return state;

  const frontBonusRoll = state.meta.pendingBonusRolls?.[0];
  if (frontBonusRoll) {
    const after = resolveBonusRollForAI(state, frontBonusRoll.playerId);
    if (after !== state) return after;
  }

  const aiNeighborChoice = (state.meta.pendingNeighborChoices || []).find((c) => state.players.find((pl) => pl.id === c.playerId)?.isAI);
  if (aiNeighborChoice) {
    const after = resolveNeighborChoiceForAI(state, aiNeighborChoice.playerId);
    if (after !== state) return after;
  }

  for (const p of state.players) {
    if (!p.isAI) continue;
    // pendingArrowChoices/pendingSectorPicks/pendingDeckPeeks/
    // pendingChargeChoices/pendingMoveChoices included here too - the
    // same reactive-on-another-player's-roll path above can just as
    // easily owe any of these to a PASSIVE AI player (not just a human),
    // and resolvePendingSpends already knows how to answer its own (see
    // its own comment) - it just never used to get called for this case,
    // since none of these were part of this hasPending check before.
    const hasPending = (state.meta.pendingClaims || []).some((c) => c.playerId === p.id)
      || (state.meta.pendingExtraBuys || []).some((eb) => eb.playerId === p.id)
      || (state.meta.pendingArrowChoices || []).some((c) => c.playerId === p.id)
      || (state.meta.pendingSectorPicks || []).some((sp) => sp.playerId === p.id)
      || (state.meta.pendingDeckPeeks || []).some((dp) => dp.playerId === p.id)
      || (state.meta.pendingChargeChoices || []).some((cc) => cc.playerId === p.id)
      || (state.meta.pendingMoveChoices || []).some((mc) => mc.playerId === p.id)
      || (state.meta.pendingCardSwapChoices || []).some((cs) => cs.playerId === p.id)
      || (state.meta.pendingEmptySectorChoices || []).some((es) => es.playerId === p.id)
      || (state.meta.pendingCardGiftChoices || []).some((cg) => cg.playerId === p.id)
      || (state.meta.pendingShipDiscards || []).some((sd) => sd.playerId === p.id)
      || (state.meta.pendingDieRewardChoices || []).some((dr) => dr.playerId === p.id)
      || (state.meta.pendingCardTradeChoices || []).some((ct) => ct.playerId === p.id);
    if (!hasPending) continue;
    const after = resolvePendingSpends(state, p.id);
    if (after !== state) return after;
  }

  if (state.meta.dice && state.meta.turnPhase === 'awaiting-allocation') {
    const allocator = state.players.find((p) => !state.meta.dice.allocations[p.id]);
    if (allocator?.isAI) return resolveAllocationForAI(state, allocator.id);
  }

  const activeIndex = state.meta.activePlayerIndex;
  for (let i = 0; i < state.players.length; i += 1) {
    if (i === activeIndex || !state.players[i].isAI) continue;
    const after = tryUseReadyAbilities(state, state.players[i].id);
    if (after !== state) return after;
  }

  const active = state.players[activeIndex];
  if (active?.isAI && state.meta.turnPhase !== 'awaiting-allocation') {
    return playAITurn(state, active.id);
  }
  return state;
}

// Repeatedly applies driveAITurnsStep, persisting (and pausing briefly)
// after each real unit of progress, so multiple AI players/turns in a row
// are visible one at a time rather than jumping straight to the next
// human decision point - same reasoning as the thunderstone repo's own
// 700ms pause between AI turns ("I just clicked to do a village turn and
// for some reason I drew a longspear and destroyed a shrouded cadaver" -
// the whole AI turn had resolved invisibly before the first repaint).
// AI_STEP_LIMIT is a runaway-bug backstop, not a real cap - a normal game
// (even several AI players deep in a row) finishes in a handful of steps.
async function driveAITurns(persist, initialState) {
  let state = initialState;
  for (let i = 0; i < AI_STEP_LIMIT; i += 1) {
    const next = driveAITurnsStep(state);
    if (next === state) return state;
    state = next;
    // eslint-disable-next-line no-await-in-loop -- each step depends on the previous one's resulting state, and the pause is deliberate pacing.
    await persist(state);
    // eslint-disable-next-line no-await-in-loop -- deliberate pacing, not a batchable operation.
    await new Promise((resolve) => { setTimeout(resolve, AI_STEP_PAUSE_MS); });
  }
  return state;
}


// Async play (asyncPlay.js, 2026-10-04): a remote room with meta.async waits
// for people instead of anyone playing on; after every state write it says
// who it waits on. The active seat; an AI seat waits on the lowest-seat
// human, who drives it when they open the room.
const asyncAdapter = {
  waitingOnOf: (g) => {
    const active = g?.players?.[g.meta.activePlayerIndex];
    if (!active) return [];
    if (!active.isAI) return [active.id];
    const humans = (g.players || []).filter((p) => !p.isAI);
    return humans.length ? [humans[0].id] : [];
  },
  isOver: (g) => Boolean(g?.meta?.winnerIds),
};
const recordWaiting = (gameId, gameState, meta) => {
  if (!db || !gameId || !meta?.async || !gameState) return;
  update(dbRef(db, `${REMOTE_GAMES_KEY}/${gameId}/meta`), waitingPatch(gameState, meta, asyncAdapter)).catch(() => {});
};

export default createStore({
  state: {
    gameState: null,
    // Matt: "you should put in an undo button for anything up to the
    // turn ending or new information being revealed." Single-step only -
    // the gameState from immediately before the LAST dispatched action,
    // or null once that action was a ROLL_DICE/END_TURN (new randomness
    // revealed, or a turn already committed - see submitAction's own
    // comment) or once undo itself has already been used once.
    undoState: null,
    remoteGameId: null,
    remoteGameMeta: null,
    remoteError: null,
    remotePresence: {},
    user: null,
    authReady: false,
    // Matt: "build in tutorials... interactive, not just rulebook dumps."
    // True only while a tutorial-launched game is active - GameBoard.vue
    // reads this to mount TutorialOverlay.vue. Deliberately NOT persisted
    // (see startTutorial/exitTutorial below, which skip saveLocalGame) -
    // a tutorial run is disposable, not a "resume where I left off" game.
    tutorialActive: false,
    // Site-wide login+approval gate (see store/siteAccess.js) - shared
    // with the thunderstone repo via the same Firebase project.
    siteUser: null,
    siteUserReady: false,
    allSiteUsers: {},
  },
  getters: {
    isLoggedIn: (state) => Boolean(state.user),
    isApproved: (state) => state.siteUser?.status === 'approved',
    isSiteAdmin: (state) => Boolean(state.siteUser?.isAdmin),
    pendingSiteUsers: (state) => Object.entries(state.allSiteUsers || {})
      .filter(([, u]) => u.status === 'pending')
      .map(([uid, u]) => ({ uid, ...u })),
  },
  mutations: {
    SET_GAME_STATE(state, gameState) {
      state.gameState = gameState;
    },
    setUndoState: (state, s) => { state.undoState = s; },
    setTutorialActive: (state, v) => { state.tutorialActive = v; },
    setUser: (state, user) => { state.user = user; },
    setAuthReady: (state, v) => { state.authReady = v; },
    setRemoteGameId: (state, id) => { state.remoteGameId = id; },
    setRemoteGameMeta: (state, meta) => { state.remoteGameMeta = meta; },
    setRemoteError: (state, err) => { state.remoteError = err; },
    setRemotePresence: (state, presence) => { state.remotePresence = presence || {}; },
    setSiteUser: (state, siteUser) => { state.siteUser = siteUser; },
    setSiteUserReady: (state, v) => { state.siteUserReady = v; },
    setAllSiteUsers: (state, users) => { state.allSiteUsers = users; },
  },
  actions: {
    // Lifetime stats, once per finished game. Safe to dispatch as often as
    // anything notices the game has ended - the fingerprint transaction in
    // userStats.js is what makes it exactly-once, not the caller.
    recordMyStats({ state }) {
      const outcome = outcomeOf({
        gameState: state.gameState,
        remoteGameId: state.remoteGameId,
        user: state.user,
      });
      if (!outcome) return;
      recordFinishedGame({
        uid: state.user?.uid,
        name: state.user?.displayName || state.user?.email || null,
        fingerprint: fingerprintOf({
          gameState: state.gameState, remoteGameId: state.remoteGameId,
        }),
        outcome,
      });
    },

    async startLocalGame({ commit }, { players, seed }) {
      trackEvent('local-game-started');
      const gameState = createGameState(players, seed);
      commit('SET_GAME_STATE', gameState);
      commit('setUndoState', null);
      saveLocalGame(gameState);
      // In case seat 1 itself is AI (an all-AI spectator setup) - the
      // normal case (a human seated first) is an immediate no-op, since
      // driveAITurnsStep only ever finds work for an AI active player.
      await driveAITurns(async (s) => { commit('SET_GAME_STATE', s); saveLocalGame(s); }, gameState);
    },

    // Matt: "build in tutorials... interactive, not just rulebook dumps."
    // A fixed seed (TUTORIAL_SEED, spaceBaseSteps.js), not a random one -
    // chosen so the player (p1) always goes first with no pending setup
    // placement to resolve before the walkthrough starts. Deliberately
    // skips saveLocalGame - unlike a real game, a tutorial run isn't
    // meant to survive a refresh/resume; exiting or finishing it just
    // clears back to the landing page.
    startTutorial({ commit }) {
      const gameState = createGameState(
        [
          { id: 'p1', name: 'You', isAI: false },
          { id: 'p2', name: 'Computer', isAI: true, difficulty: 'normal' },
        ],
        TUTORIAL_SEED,
      );
      commit('SET_GAME_STATE', gameState);
      commit('setUndoState', null);
      commit('setTutorialActive', true);
    },

    // "Skip tutorial" - bails out entirely, back to the landing page.
    // Distinct from finishTutorial below.
    exitTutorial({ commit }) {
      commit('setTutorialActive', false);
      commit('SET_GAME_STATE', null);
      commit('setUndoState', null);
    },

    // Reaching the tutorial's last step, instead - the walkthrough is
    // done, but there's no reason to throw away the game it was just
    // played on. Just drops the overlay and starts persisting from here
    // on, same as any other local game (saveLocalGame wasn't called
    // during the tutorial itself - see startTutorial's own comment).
    finishTutorial({ commit, state }) {
      commit('setTutorialActive', false);
      saveLocalGame(state.gameState);
    },

    loadLocalGame({ commit }) {
      const saved = loadLocalGame();
      if (saved) commit('SET_GAME_STATE', saved);
    },
    // The only place engineDispatch() gets called from the UI side - every
    // human action goes through this one funnel, same convention as the
    // thunderstone repo's own store/index.js submitAction. persist()
    // branches local-storage vs remote-Firebase the same way that repo's
    // own submitAction does, and is reused by driveAITurns below so an AI
    // player's own turns sync the exact same way a human's would.
    async submitAction({ state, commit }, action) {
      if (!state.gameState) return;
      const before = state.gameState;
      const next = engineDispatch(before, action);
      if (next === before) return; // illegal/no-op move - nothing actually happened, nothing to snapshot either.

      // Matt: "an undo button for anything up to the turn ending or new
      // information being revealed." ROLL_DICE reveals new randomness;
      // END_TURN is its own commitment point - neither this action NOR
      // whatever came before it should be undoable once either happens.
      //
      // Matt reported this directly, a real exploit found live: "when I
      // click undo and then roll it again, I get different numbers,
      // which means I could cheat and just keep undoing until I get the
      // numbers I want." The literal ACTION_TYPES.ROLL_DICE check above
      // only ever covered the one action explicitly named that - it
      // completely missed every OTHER action that can also consume
      // randomness as a side effect: a bought card granting a Bonus Roll
      // (BUY_CARD), a chargeable bonusRoll/rerollDice ability
      // (USE_ABILITY), or a hit chain that grants one (ALLOCATE_DICE) -
      // any of those left the pre-action snapshot undoable, so undoing
      // one and redoing it drew an entirely new random outcome each
      // time. Comparing meta.rngState.a before/after catches ALL of
      // these uniformly (it advances on every single random draw this
      // engine ever makes - dice, shuffles, bonus rolls alike - see
      // rng.js's own comment on why every draw threads through this one
      // value), including future RNG-consuming mechanics that might not
      // exist yet, without needing a hand-maintained list of action
      // types that happen to touch randomness.
      const revealsInfo = action.type === ACTION_TYPES.ROLL_DICE
        || action.type === ACTION_TYPES.END_TURN
        || before.meta.rngState?.a !== next.meta.rngState?.a;
      commit('setUndoState', revealsInfo ? null : before);

      const persist = async (s) => {
        commit('SET_GAME_STATE', s);
        if (state.remoteGameId) {
          if (!db) throw new Error('Firebase is not configured (see .env.example).');
          // Optimistic local commit above for snappy UX; the RTDB
          // listener will echo the same state back and re-commit it (a
          // harmless no-op diff) once the write round-trips.
          await set(dbRef(db, `${REMOTE_GAMES_KEY}/${state.remoteGameId}/state`), JSON.stringify(s));
          recordWaiting(state.remoteGameId, s, state.remoteGameMeta);
        } else {
          saveLocalGame(s);
        }
      };

      await persist(next);
      // Any AI progress after this point is new game state a human
      // can't retroactively undo through either - clears the same way
      // ROLL_DICE/END_TURN do above.
      const beforeAITurns = state.gameState;
      await driveAITurns(persist, next);
      if (state.gameState !== beforeAITurns) commit('setUndoState', null);
    },
    // Single-step only (see undoState's own comment) - reverts to
    // whatever gameState was immediately before the last dispatched
    // action, then forgets it (no redo, no multi-level undo stack).
    async undo({ state, commit }) {
      if (!state.undoState) return;
      const restored = state.undoState;
      commit('setUndoState', null);
      commit('SET_GAME_STATE', restored);
      if (state.remoteGameId) {
        if (!db) throw new Error('Firebase is not configured (see .env.example).');
        await set(dbRef(db, `${REMOTE_GAMES_KEY}/${state.remoteGameId}/state`), JSON.stringify(restored));
        recordWaiting(state.remoteGameId, restored, state.remoteGameMeta);
      } else {
        saveLocalGame(restored);
      }
    },
    endLocalGame({ commit }) {
      commit('SET_GAME_STATE', null);
      commit('setUndoState', null);
      clearLocalGame();
    },
    // Matt: "as long as you're doing that, it'll be also great to be able
    // to set up remote games. You can look to how we did it for
    // Thunderstone to see how to do it, a similar pattern." Ported
    // directly from that repo's own createRemoteGame - see this file's
    // own top-of-file comment for the RTDB namespace this uses instead.
    async createRemoteGame({ state, dispatch }, { maxPlayers = 2, async: asyncRoom = false } = {}) {
      trackEvent('room-hosted');
      if (!db) throw new Error('Firebase is not configured (see .env.example).');
      if (!state.user) throw new Error('Sign in first.');

      const gameRef = push(dbRef(db, REMOTE_GAMES_KEY));
      const gameId = gameRef.key;

      let code;
      let attempts = 0;
      do {
        code = generateRoomCode(attempts);
        attempts += 1;
        // eslint-disable-next-line no-await-in-loop -- each retry depends on the previous attempt's collision check.
      } while (attempts < 10 && (await get(dbRef(db, `${REMOTE_GAME_CODES_KEY}/${code}`))).exists());

      const meta = {
        hostUid: state.user.uid,
        status: 'lobby',
        async: asyncRoom,
        // Space Base supports 2-7 players (see createGameState's own
        // comment) - clamped here, not just in the UI, since this is the
        // actual system boundary a malformed/bypassed client request
        // would hit.
        maxPlayers: Math.min(Math.max(maxPlayers, 2), 7),
        roomCode: code,
        createdAt: new Date().toISOString(),
        playerSlots: [{
          uid: state.user.uid, displayName: state.user.displayName || 'Host', seatIndex: 0, connected: true,
        }],
      };
      await set(dbRef(db, `${REMOTE_GAMES_KEY}/${gameId}/meta`), meta);
      await set(dbRef(db, `${REMOTE_GAME_CODES_KEY}/${code}`), gameId);
      await set(dbRef(db, `${REMOTE_USER_GAMES_KEY}/${state.user.uid}/${gameId}`), { role: 'host', joinedAt: meta.createdAt });

      await dispatch('bindRemoteGame', gameId);
      return { gameId, code };
    },

    async joinRemoteGame({ state, dispatch }, { code }) {
      trackEvent('room-joined');
      if (!db) throw new Error('Firebase is not configured (see .env.example).');
      if (!state.user) throw new Error('Sign in first.');

      const normalized = normalizeRoomCode(code);
      const codeSnap = await get(dbRef(db, `${REMOTE_GAME_CODES_KEY}/${normalized}`));
      if (!codeSnap.exists()) throw new Error(`No game found for code "${normalized}".`);
      const gameId = codeSnap.val();

      const metaSnap = await get(dbRef(db, `${REMOTE_GAMES_KEY}/${gameId}/meta`));
      if (!metaSnap.exists()) throw new Error('That game no longer exists.');
      const meta = metaSnap.val();
      if (meta.status !== 'lobby') throw new Error('That game has already started.');

      const slots = meta.playerSlots || [];
      if (!slots.some((s) => s.uid === state.user.uid)) {
        if (slots.length >= (meta.maxPlayers || 2)) throw new Error('That game is full.');
        slots.push({
          uid: state.user.uid, displayName: state.user.displayName || 'Guest', seatIndex: slots.length, connected: true,
        });
        await update(dbRef(db, `${REMOTE_GAMES_KEY}/${gameId}/meta`), { playerSlots: slots });
        await set(dbRef(db, `${REMOTE_USER_GAMES_KEY}/${state.user.uid}/${gameId}`), { role: 'guest', joinedAt: new Date().toISOString() });
      }

      await dispatch('bindRemoteGame', gameId);
      return { gameId };
    },

    // Host-only - transitions the lobby into a real game once at least 2
    // players have joined. Seed is host-authoritative (chosen once here,
    // not re-rolled per client) so every peer's engine produces the exact
    // same shuffled shipyard/starting draws from the same written state.
    async startRemoteGame({ state }) {
      if (!db || !state.remoteGameId) return;
      const meta = state.remoteGameMeta;
      if (!meta || meta.hostUid !== state.user?.uid) throw new Error('Only the host can start the game.');
      if ((meta.playerSlots || []).length < 2) throw new Error('Waiting for a second player to join.');

      const playerConfigs = [...meta.playerSlots]
        .sort((a, b) => a.seatIndex - b.seatIndex)
        .map((slot) => ({ id: slot.uid, name: slot.displayName }));
      const seed = Math.floor(Math.random() * (2 ** 31));
      const gameState = createGameState(playerConfigs, seed);

      await set(dbRef(db, `${REMOTE_GAMES_KEY}/${state.remoteGameId}/state`), JSON.stringify(gameState));
      await update(dbRef(db, `${REMOTE_GAMES_KEY}/${state.remoteGameId}/meta`), {
        status: 'in-progress',
        lastActiveAt: Date.now(),
        // An async room says who it waits on from the first turn (asyncPlay.js).
        ...(state.remoteGameMeta?.async ? waitingPatch(gameState, state.remoteGameMeta, asyncAdapter) : {}),
      });
    },

    bindRemoteGame({ commit, state }, gameId) {
      if (!db) throw new Error('Firebase is not configured (see .env.example).');
      detach('remoteMeta');
      detach('remoteState');
      detach('remotePresence');
      commit('setRemoteGameId', gameId);
      commit('setRemoteError', null);
      commit('setUndoState', null);

      unsubscribers.remoteMeta = onValue(dbRef(db, `${REMOTE_GAMES_KEY}/${gameId}/meta`), (snap) => {
        commit('setRemoteGameMeta', snap.val());
      });
      unsubscribers.remoteState = onValue(dbRef(db, `${REMOTE_GAMES_KEY}/${gameId}/state`), (snap) => {
        // Written as a JSON string (see the set() calls throughout this
        // file) so RTDB never gets a chance to prune empty arrays/objects
        // out of the tree - it has no native "empty array" representation
        // and silently drops such keys otherwise (e.g. a sector with no
        // deployedStack yet).
        const raw = snap.val();
        const next = raw ? JSON.parse(raw) : null;
        // Matt reported this directly, a real exploit found live: "it was
        // not my turn, but I was able to click the undo button which
        // undid my opponents choice on their board." undoState only ever
        // meant "the state right before MY OWN last local action" - but
        // nothing here ever invalidated it once someone ELSE's move
        // landed on top of it, so a passive player's own stale undoState
        // (from however many of their own actions ago) stayed sitting
        // there, fully armed, through the active player's ENTIRE
        // subsequent turn. Clicking Undo then reverted the whole shared
        // game back to before that stale snapshot - wiping out everyone
        // else's progress since, not just "my own last action" the
        // button claims to undo.
        //
        // submitAction's own persist() already commits SET_GAME_STATE
        // optimistically, synchronously, before its Firebase write even
        // starts (see its own comment) - so by the time THIS device's
        // own write echoes back through this exact listener, its content
        // is already reflected in state.gameState and this comparison is
        // a no-op (undoState correctly survives its own echo). Only a
        // genuinely foreign update - one this device didn't already
        // know about, i.e. actually came from another player's device -
        // ever differs from the currently-held state here, which is
        // exactly when undoState needs to be thrown away.
        if (JSON.stringify(next) !== JSON.stringify(state.gameState)) {
          commit('setUndoState', null);
        }
        commit('SET_GAME_STATE', next);
      });
      unsubscribers.remotePresence = onValue(dbRef(db, `${REMOTE_GAMES_KEY}/${gameId}/presence`), (snap) => {
        commit('setRemotePresence', snap.val());
      });

      // Mark ourselves present, and let Firebase itself clear that the
      // instant this connection actually drops (closed tab, lost network,
      // crash) - far more reliable than any explicit "I'm leaving" write.
      if (state.user) {
        const myPresenceRef = dbRef(db, `${REMOTE_GAMES_KEY}/${gameId}/presence/${state.user.uid}`);
        onDisconnect(myPresenceRef).remove();
        set(myPresenceRef, true);
      }
    },

    unbindRemoteGame({ commit, dispatch, state }) {
      if (db && state.remoteGameId && state.user) {
        remove(dbRef(db, `${REMOTE_GAMES_KEY}/${state.remoteGameId}/presence/${state.user.uid}`));
      }
      detach('remoteMeta');
      detach('remoteState');
      detach('remotePresence');
      commit('setRemoteGameId', null);
      commit('setRemoteGameMeta', null);
      commit('setRemotePresence', {});
      // Not SET_GAME_STATE(null) - a local game may still be saved (see
      // loadLocalGame) and leaving a remote game shouldn't hide it.
      dispatch('loadLocalGame');
    },
    // Ported from the thunderstone repo's own store/index.js - see its
    // CLAUDE.md "Site access" section for the full design (shared
    // siteUsers key, admin-approval gate, the mattgrosso@gmail.com
    // bootstrap-admin rule).
    initAuth({ commit }) {
      if (!auth) { commit('setAuthReady', true); commit('setSiteUserReady', true); return; }
      detach('siteUser');
      detach('allSiteUsers');
      onAuthStateChanged(auth, async (user) => {
        if (await settleSharedSignIn(auth, user)) return; // see utils/sharedSignIn.js
        // An anonymous session exists only so a signed-out bug report can
        // satisfy the inbox's auth != null rule (utils/bugReports.js has
        // always minted one). It is NOT a player: without this filter, filing
        // a bug while signed out left the app looking signed in as nobody.
        if (user?.isAnonymous) {
          commit('setUser', null);
          commit('setAuthReady', true);
          commit('setSiteUser', null);
          commit('setSiteUserReady', true);
          return;
        }
        commit('setUser', user
          ? { uid: user.uid, displayName: user.displayName, email: user.email, photoURL: user.photoURL }
          : null);
        if (user?.email) writeLastSignInEmail(user.email);
        commit('setAuthReady', true);
        detach('siteUser');
        detach('allSiteUsers');
        if (!user) {
          commit('setSiteUser', null);
          commit('setAllSiteUsers', {});
          commit('setSiteUserReady', true);
          return;
        }

        // Deliberately NOT awaited (was `await ensureSiteUserRecord(user)`
        // here) - this does its own network get()/set() round trip, which
        // rejects but only after a real connection attempt when there's no
        // connectivity (get() "rejects if the client is unable to return a
        // value" per its own doc comment - the underlying repoGetValue call
        // has to actually try and fail to reach the server first, not an
        // instant failure). Awaiting it here would gate the offline-fallback
        // timer below on that same network attempt, defeating the entire
        // point of the fallback: someone genuinely offline needs to fall
        // back to their cached approval right away, not queue up behind one
        // more thing that also can't complete without a connection. This
        // only ever creates the record once (a no-op after that - see its
        // own comment) and every fallback path below is independent of it.
        ensureSiteUserRecord(user).catch(() => {});

        const { uid } = user;
        // Set the instant any value (even null) actually arrives from the
        // live subscription, so the fallback below never clobbers real,
        // current data with a stale cache once the live answer is in.
        let liveResolved = false;

        unsubscribers.siteUser = watchSiteUserStatus(uid, (record) => {
          liveResolved = true;
          commit('setSiteUser', record);
          commit('setSiteUserReady', true);
          // No-ops unless record.status === 'approved' (see
          // cacheApprovedSiteUser's own guard) - never caches
          // 'pending'/'denied' as a fallback-worthy state.
          cacheApprovedSiteUser(uid, record);
          if (record?.isAdmin) {
            detach('allSiteUsers');
            unsubscribers.allSiteUsers = watchAllSiteUsers((users) => commit('setAllSiteUsers', users));
          } else {
            detach('allSiteUsers');
            commit('setAllSiteUsers', {});
          }
        });

        // Offline-approval-gate fallback (see siteAccess.js's own comment
        // on cacheApprovedSiteUser/readCachedSiteUser, and
        // SITE_USER_FALLBACK_TIMEOUT_MS's own comment above). The live
        // subscription above is left running regardless - if the device
        // comes back online and something changed (Matt revoked access),
        // it fires again and overwrites whatever this fallback committed.
        const tryOfflineFallback = () => {
          if (liveResolved) return;
          const cached = readCachedSiteUser(uid);
          if (!cached) return; // Nothing to fall back to - stay on the spinner rather than guess.
          commit('setSiteUser', { status: 'approved', isAdmin: cached.isAdmin });
          commit('setSiteUserReady', true);
        };
        if (typeof navigator !== 'undefined' && navigator.onLine === false) {
          tryOfflineFallback();
        } else {
          setTimeout(tryOfflineFallback, SITE_USER_FALLBACK_TIMEOUT_MS);
        }
      });
    },
    async login() {
      if (!auth) throw new Error('Firebase is not configured (see .env.example).');
      // Not cross-domain SSO (see lastSignIn.js) - just biases/pre-fills
      // Google's account chooser toward whichever account last signed in
      // successfully on this origin, so a repeat sign-in on a
      // multi-account browser doesn't start from a blank chooser.
      const lastEmail = readLastSignInEmail();
      googleProvider.setCustomParameters(lastEmail ? { login_hint: lastEmail } : {});
      markInteractiveSignIn();
      await signInWithPopup(auth, googleProvider);
    },
    async logout({ commit }) {
      if (!auth) return;
      await endSharedSignIn();
      await signOut(auth);
      commit('setUser', null);
    },
    async approveSiteUser(_, uid) {
      await setSiteUserStatus(uid, 'approved');
    },
    async denySiteUser(_, uid) {
      await setSiteUserStatus(uid, 'denied');
    },
  },
});

export { ACTION_TYPES };
// Exported for direct unit testing only (src/test/store/driveAITurns.test.js)
// - every other caller goes through the store's own actions above.
// driveAITurnsStep has no Vuex dependency itself (pure state -> state, same
// shape as the engine's own dispatch()), so testing it directly avoids
// needing a real store/persist/timer harness just to exercise one step of
// AI-turn logic.
export { driveAITurnsStep };
